import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import { App } from '@gin-betting/core';
import restrictions from '@gin-betting/restrictions';
import { createStructuredSelector } from 'reselect';

import { Modules, useAsyncModule } from 'async-modules';
import type { State } from 'types';
import { useChangeStake, usePlaceBet } from '../../../hooks';
import { RestrictionsContext } from '../../../restrictions';
import { makeSelectIsFreebet, selectFirstBetslipOdd } from '../../../selectors';

const {
    selectors: { selectBetslipBetWithStatus, selectBetslipBetTotals },
} = betslip;
const {
    selectors: { selectLowerMaxBetBetslipBet },
} = restrictions;

interface Input {
    betslipBetId: string;
}

interface Output {
    singleStake: string;
    isFreebet: boolean;
    totals: ReturnType<typeof selectBetslipBetTotals>;
    maxStake: string;
    changeStakeHandler: (stake: string) => void;
    isSuspended: boolean;
    betslipOddIds: string[];
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const useSinglePart = ({ betslipBetId }: Input): Output => {
    const freebetModule = useAsyncModule('freebet');
    const changeStakeHandler = useChangeStake({ betslipBetId });

    const { state: restrictionsState } = useContext(RestrictionsContext);
    const selectProps = useMemo(() => makeSelectProps(freebetModule), [
        freebetModule,
    ]);
    const {
        betslipOdd,
        totals,
        betslipBetWithStatus,
        maxBet,
        isFreebet,
    } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState })
    );

    const maxStake = maxBet || '';
    const status = betslipBetWithStatus?.status;
    const isProcessing = status === App.BetslipStatus.PROCESSING;
    const isSuccess = status === App.BetslipStatus.SUCCESS;
    const isSuspended = isProcessing || isSuccess;
    const betslipOddIds = betslipBetWithStatus?.betslipOddIds;

    const singleStake = betslipOdd?.singleStake || '';

    const { onKeyDown } = usePlaceBet({
        totalStake: +singleStake,
        betslipBetId,
    });

    return {
        changeStakeHandler,
        maxStake,
        singleStake,
        isFreebet,
        totals,
        isSuspended,
        betslipOddIds: betslipOddIds?.toArray() ?? [],
        onKeyDown,
    };
};

const makeSelectProps = (
    freebetModule: Modules['freebet']['default'] | null
) => {
    return createStructuredSelector({
        betslipOdd: selectFirstBetslipOdd,
        totals: selectBetslipBetTotals,
        maxBet: selectLowerMaxBetBetslipBet,
        isFreebet: makeSelectIsFreebet(freebetModule),
        betslipBetWithStatus: selectBetslipBetWithStatus,
    });
};

export default useSinglePart;
